import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * CalendarTimeline — a reusable calendar-grid layout shared between planning
 * views. Renders a time axis (periods + days) with one row per "thing"
 * (person, resource, ...), each row showing a set of horizontal bars that
 * span day-index ranges.
 *
 * Optional drag interactions:
 * - **drag-to-create**: enabled by passing `onRangeCreate`. Mousedown on
 *   empty timeline space + drag → calls `onRangeCreate(rowKey, start, end)`
 *   on mouseup. A ghost bar previews the proposed range during the drag.
 * - **edge-resize**: per-bar opt-in via `bar.resizable`. Resizable bars get
 *   ~6px col-resize handles on each edge. Dragging an edge previews the new
 *   range live and calls `onRangeResize(rowKey, barKey, start, end)` on
 *   mouseup. Body-clicks still fire the bar's `onClick` (so editing remains
 *   reachable).
 *
 * Callers own:
 *  - building the `days`/`periods` arrays
 *  - producing each row's `bars`
 *  - how the left label column looks (`labelContent`)
 *  - optional per-row extras in the timeline area (e.g. "show suggestions")
 *
 * This component owns:
 *  - period-label header + striped background bands
 *  - weekend shading
 *  - "today" vertical line
 *  - bar positioning, hover tooltip, drag/resize gestures
 */

export interface CalendarDay {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** Short display label (e.g. "Mon", "Mar 3"). Used to detect weekends by prefix. */
  label: string;
  /** Index of the period this day belongs to. */
  period_idx: number;
}

export interface CalendarPeriod {
  label: string;
}

/**
 * A single bar on a row. Variants are opaque strings; the caller provides a
 * style map in `variantStyles`. This keeps bar styling caller-controlled
 * while keeping the layout component variant-agnostic.
 */
export interface CalendarBar {
  key: string;
  label: string;
  startIdx: number;
  endIdx: number;
  variant: string;
  /** Optional small right-aligned text on the bar (e.g. effort or count). */
  meta?: string;
  /** Extra text shown in the tooltip after "label — meta". */
  tooltipSuffix?: string;
  onClick?: () => void;
  /** Inline style override (e.g. to offset vertically when multiple bars share a row). */
  topPx?: number;
  /** When true, the bar shows edge-resize handles. Default false. */
  resizable?: boolean;
}

export interface CalendarRow {
  key: string;
  /** Rendered in the left fixed-width label column. */
  labelContent: React.ReactNode;
  bars: CalendarBar[];
  /**
   * Optional per-row overlay rendered over the timeline area. Use for
   * row-specific affordances (e.g. PlanningGrid's "show suggestions" links).
   * Receives the period boundary metadata so callers can position elements.
   */
  timelineOverlay?: (ctx: TimelineOverlayContext) => React.ReactNode;
}

export interface TimelineOverlayContext {
  periodBoundaries: PeriodBoundary[];
  totalDays: number;
  days: CalendarDay[];
}

export interface VariantStyle {
  /** Tailwind classes for the default state. */
  idle: string;
  /** Tailwind classes when hovered. */
  hover: string;
}

interface PeriodBoundary {
  leftPct: number;
  widthPct: number;
  label: string;
  startIdx: number;
  endIdx: number;
}

interface CalendarTimelineProps {
  days: CalendarDay[];
  periods: CalendarPeriod[];
  rows: CalendarRow[];
  variantStyles: Record<string, VariantStyle>;
  labelColumnWidthPx?: number;
  /** Shown when `rows` or `days` is empty. */
  emptyMessage?: string;
  /** Minimum row height (px). The row grows beyond this for stacked bars. */
  minRowHeightPx?: number;
  /** Vertical pixel spacing between stacked bars in a row. */
  barStackSpacingPx?: number;
  /**
   * Enable drag-to-create. Called on mouseup with the inclusive day-index
   * range that was dragged. Cancelled by ESC.
   */
  onRangeCreate?: (rowKey: string, startIdx: number, endIdx: number) => void;
  /**
   * Enable edge-resize on bars whose `resizable` is true. Called on mouseup
   * with the new inclusive day-index range. Cancelled by ESC.
   */
  onRangeResize?: (
    rowKey: string,
    barKey: string,
    startIdx: number,
    endIdx: number,
  ) => void;
}

// ── Drag state ──────────────────────────────────────────────────────

type DragState =
  | { kind: 'create'; rowKey: string; anchorIdx: number; cursorIdx: number; anchorClientX: number; cursorClientX: number }
  | {
      kind: 'resize';
      rowKey: string;
      barKey: string;
      edge: 'start' | 'end';
      originalStart: number;
      originalEnd: number;
      cursorIdx: number;
    };

/** Pixels of cursor movement required to treat a mouseup as a "drag" (using
 *  the dragged range) vs a "click" (using the clicked period's range). Below
 *  this threshold the gesture is a click. */
const CREATE_DRAG_THRESHOLD_PX = 4;

/** Width (px) of each edge resize-handle on a resizable bar. */
const RESIZE_HANDLE_WIDTH_PX = 6;

function clientXToIdx(clientX: number, el: HTMLElement, totalDays: number): number {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) return 0;
  const ratio = (clientX - rect.left) / rect.width;
  return Math.max(0, Math.min(totalDays - 1, Math.floor(ratio * totalDays)));
}

// ── Component ───────────────────────────────────────────────────────

export const CalendarTimeline: React.FC<CalendarTimelineProps> = ({
  days,
  periods,
  rows,
  variantStyles,
  labelColumnWidthPx = 160,
  emptyMessage = 'No data.',
  minRowHeightPx = 44,
  barStackSpacingPx = 22,
  onRangeCreate,
  onRangeResize,
}) => {
  const totalDays = days.length;
  const [dragState, setDragState] = useState<DragState | null>(null);
  // Element of the timeline whose row owns the current drag — needed for
  // px→idx geometry on document-level mousemove.
  const dragTimelineRef = useRef<HTMLElement | null>(null);

  // Latest `periodBoundaries` accessible from the document-level mouseup
  // handler (which captures stale closures across renders).
  const periodBoundariesRef = useRef<PeriodBoundary[]>([]);

  const periodBoundaries = useMemo<PeriodBoundary[]>(() => {
    const boundaries: PeriodBoundary[] = [];
    for (let pi = 0; pi < periods.length; pi++) {
      const pDays = days.filter((d) => d.period_idx === pi);
      if (pDays.length === 0) continue;
      const firstIdx = days.indexOf(pDays[0]);
      const lastIdx = days.indexOf(pDays[pDays.length - 1]);
      boundaries.push({
        leftPct: (firstIdx / totalDays) * 100,
        widthPct: (pDays.length / totalDays) * 100,
        label: periods[pi].label,
        startIdx: firstIdx,
        endIdx: lastIdx,
      });
    }
    return boundaries;
  }, [days, periods, totalDays]);

  useEffect(() => {
    periodBoundariesRef.current = periodBoundaries;
  }, [periodBoundaries]);

  const today = new Date().toISOString().slice(0, 10);
  const todayIdx = days.findIndex((d) => d.date === today);
  const todayPct = todayIdx >= 0 ? ((todayIdx + 0.5) / totalDays) * 100 : -1;

  // Strip the current year from period labels when it matches — keeps the axis uncluttered.
  const currentYear = new Date().getFullYear().toString();
  const shortenedPeriodLabels = useMemo(
    () => periodBoundaries.map((pb) => pb.label.replace(` ${currentYear}`, '')),
    [periodBoundaries, currentYear],
  );

  // ─── Drag handlers ────────────────────────────────────────────────
  // Document-level listeners live for the duration of an active drag so the
  // gesture keeps tracking even when the cursor leaves the timeline rect.
  useEffect(() => {
    if (!dragState) return;

    const onMove = (e: MouseEvent) => {
      const tl = dragTimelineRef.current;
      if (!tl) return;
      const idx = clientXToIdx(e.clientX, tl, totalDays);
      setDragState((prev) => {
        if (!prev) return prev;
        if (prev.kind === 'create') {
          return { ...prev, cursorIdx: idx, cursorClientX: e.clientX };
        }
        return { ...prev, cursorIdx: idx };
      });
    };

    const onUp = () => {
      setDragState((prev) => {
        if (!prev) return null;
        if (prev.kind === 'create') {
          const moved = Math.abs(prev.cursorClientX - prev.anchorClientX);
          const isDrag = moved >= CREATE_DRAG_THRESHOLD_PX || prev.anchorIdx !== prev.cursorIdx;
          if (isDrag) {
            const startIdx = Math.min(prev.anchorIdx, prev.cursorIdx);
            const endIdx = Math.max(prev.anchorIdx, prev.cursorIdx);
            onRangeCreate?.(prev.rowKey, startIdx, endIdx);
          } else {
            // Single click: commit the clicked period's full day range so the
            // create flow gets the planning-rhythm window the user clicked in.
            const period = periodBoundariesRef.current.find(
              (pb) => prev.anchorIdx >= pb.startIdx && prev.anchorIdx <= pb.endIdx,
            );
            if (period) {
              onRangeCreate?.(prev.rowKey, period.startIdx, period.endIdx);
            }
          }
        } else {
          let startIdx: number;
          let endIdx: number;
          if (prev.edge === 'start') {
            startIdx = Math.min(prev.cursorIdx, prev.originalEnd);
            endIdx = prev.originalEnd;
          } else {
            startIdx = prev.originalStart;
            endIdx = Math.max(prev.cursorIdx, prev.originalStart);
          }
          if (startIdx !== prev.originalStart || endIdx !== prev.originalEnd) {
            onRangeResize?.(prev.rowKey, prev.barKey, startIdx, endIdx);
          }
        }
        return null;
      });
      dragTimelineRef.current = null;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDragState(null);
        dragTimelineRef.current = null;
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keydown', onKey);
    };
  }, [dragState, totalDays, onRangeCreate, onRangeResize]);

  // Suppress text selection while dragging.
  useEffect(() => {
    if (!dragState) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.userSelect = prev;
    };
  }, [dragState]);

  if (rows.length === 0 || totalDays === 0) {
    return <div className="p-6 text-center text-cm-text-muted text-sm">{emptyMessage}</div>;
  }

  const startCreate = (rowKey: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (!onRangeCreate) return;
    // Ignore mousedowns that landed on a bar — the bar handles its own
    // click/resize gesture.
    const target = e.target as HTMLElement;
    if (target.closest('[data-cal-bar="true"]')) return;

    const tl = e.currentTarget;
    const idx = clientXToIdx(e.clientX, tl, totalDays);
    dragTimelineRef.current = tl;
    setDragState({
      kind: 'create',
      rowKey,
      anchorIdx: idx,
      cursorIdx: idx,
      anchorClientX: e.clientX,
      cursorClientX: e.clientX,
    });
  };

  const startResize = (
    rowKey: string,
    bar: CalendarBar,
    edge: 'start' | 'end',
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!onRangeResize) return;
    e.stopPropagation();
    e.preventDefault();
    const tl = (e.currentTarget.closest('[data-cal-timeline="true"]') as HTMLElement) || null;
    if (!tl) return;
    dragTimelineRef.current = tl;
    setDragState({
      kind: 'resize',
      rowKey,
      barKey: bar.key,
      edge,
      originalStart: bar.startIdx,
      originalEnd: bar.endIdx,
      cursorIdx: edge === 'start' ? bar.startIdx : bar.endIdx,
    });
  };

  return (
    <div className="overflow-x-auto border border-cm-border-subtle rounded-lg">
      {/* Header — period labels only, no day ticks */}
      <div className="flex">
        <div
          className="shrink-0 bg-cm-bg-deep border-b border-cm-border-subtle"
          style={{ width: labelColumnWidthPx }}
        />
        <div className="flex-1 relative border-b border-cm-border-subtle min-w-[600px] h-7">
          {periodBoundaries.map((pb, i) => (
            <div
              key={i}
              className={`absolute top-0 h-full flex items-center justify-center text-[10px] font-medium text-cm-text-muted border-l border-cm-border-subtle ${
                i % 2 === 1 ? 'bg-cm-bg-deep/50' : ''
              }`}
              style={{ left: `${pb.leftPct}%`, width: `${pb.widthPct}%` }}
            >
              {shortenedPeriodLabels[i]}
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      {rows.map((row) => (
        <TimelineRow
          key={row.key}
          row={row}
          days={days}
          totalDays={totalDays}
          periodBoundaries={periodBoundaries}
          todayPct={todayPct}
          variantStyles={variantStyles}
          labelColumnWidthPx={labelColumnWidthPx}
          minRowHeightPx={minRowHeightPx}
          barStackSpacingPx={barStackSpacingPx}
          dragState={dragState}
          onTimelineMouseDown={onRangeCreate ? (e) => startCreate(row.key, e) : undefined}
          onResizeMouseDown={onRangeResize ? (bar, edge, e) => startResize(row.key, bar, edge, e) : undefined}
        />
      ))}
    </div>
  );
};

// ── Internal row component ──────────────────────────────────────────

function TimelineRow({
  row,
  days,
  totalDays,
  periodBoundaries,
  todayPct,
  variantStyles,
  labelColumnWidthPx,
  minRowHeightPx,
  barStackSpacingPx,
  dragState,
  onTimelineMouseDown,
  onResizeMouseDown,
}: {
  row: CalendarRow;
  days: CalendarDay[];
  totalDays: number;
  periodBoundaries: PeriodBoundary[];
  todayPct: number;
  variantStyles: Record<string, VariantStyle>;
  labelColumnWidthPx: number;
  minRowHeightPx: number;
  barStackSpacingPx: number;
  dragState: DragState | null;
  onTimelineMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onResizeMouseDown?: (
    bar: CalendarBar,
    edge: 'start' | 'end',
    e: React.MouseEvent<HTMLDivElement>,
  ) => void;
}) {
  const [hoveredBarKey, setHoveredBarKey] = useState<string | null>(null);

  // While a bar is being resized, render it at its in-flight position so the
  // user sees live feedback. All other bars render at their persisted range.
  const effectiveBars = useMemo(() => {
    return row.bars.map((bar, i) => {
      let startIdx = bar.startIdx;
      let endIdx = bar.endIdx;
      if (
        dragState?.kind === 'resize' &&
        dragState.rowKey === row.key &&
        dragState.barKey === bar.key
      ) {
        if (dragState.edge === 'start') {
          startIdx = Math.min(dragState.cursorIdx, dragState.originalEnd);
          endIdx = dragState.originalEnd;
        } else {
          startIdx = dragState.originalStart;
          endIdx = Math.max(dragState.cursorIdx, dragState.originalStart);
        }
      }
      return {
        ...bar,
        startIdx,
        endIdx,
        resolvedTopPx: bar.topPx ?? 4 + i * barStackSpacingPx,
      };
    });
  }, [row.bars, row.key, dragState, barStackSpacingPx]);

  const rowHeight = Math.max(minRowHeightPx, row.bars.length * barStackSpacingPx + 24);

  // Ghost bar for drag-to-create: render at the bottom of the row at top = max
  // of stacked bars, so it doesn't overlap existing bars.
  const ghost =
    dragState?.kind === 'create' && dragState.rowKey === row.key
      ? (() => {
          const startIdx = Math.min(dragState.anchorIdx, dragState.cursorIdx);
          const endIdx = Math.max(dragState.anchorIdx, dragState.cursorIdx);
          return {
            startIdx,
            endIdx,
            topPx: 4 + row.bars.length * barStackSpacingPx,
          };
        })()
      : null;

  return (
    <div className="flex border-b border-cm-border-subtle">
      <div
        className="shrink-0 bg-cm-bg-deep px-3 py-2 flex items-center gap-2"
        style={{ width: labelColumnWidthPx }}
      >
        {row.labelContent}
      </div>

      <div
        data-cal-timeline="true"
        className={`flex-1 relative min-w-[600px] group/timeline ${
          onTimelineMouseDown ? 'cursor-crosshair' : ''
        }`}
        style={{ minHeight: rowHeight }}
        onMouseDown={onTimelineMouseDown}
      >
        {/* Period bands */}
        {periodBoundaries.map((pb, i) => (
          <div
            key={`band-${i}`}
            className={`absolute top-0 h-full pointer-events-none ${
              i % 2 === 1 ? 'bg-cm-bg-deep/30' : ''
            } ${i > 0 ? 'border-l border-cm-border-subtle/50' : ''}`}
            style={{ left: `${pb.leftPct}%`, width: `${pb.widthPct}%` }}
          />
        ))}

        {/* Weekend shading */}
        {days.map((d, i) => {
          if (!d.label?.startsWith('Sat') && !d.label?.startsWith('Sun')) return null;
          return (
            <div
              key={`we-${i}`}
              className="absolute top-0 h-full bg-gray-900/30 pointer-events-none"
              style={{
                left: `${(i / totalDays) * 100}%`,
                width: `${(1 / totalDays) * 100}%`,
              }}
            />
          );
        })}

        {/* Today line */}
        {todayPct >= 0 && (
          <div
            className="absolute top-0 h-full w-px bg-red-500/60 z-10 pointer-events-none"
            style={{ left: `${todayPct}%` }}
          />
        )}

        {/* Bars */}
        {effectiveBars.map((bar) => {
          const leftPct = (bar.startIdx / totalDays) * 100;
          const widthPct = ((bar.endIdx - bar.startIdx + 1) / totalDays) * 100;
          const isHovered = hoveredBarKey === bar.key;
          const style = variantStyles[bar.variant] ?? { idle: '', hover: '' };
          const isResizing =
            dragState?.kind === 'resize' &&
            dragState.rowKey === row.key &&
            dragState.barKey === bar.key;

          return (
            <React.Fragment key={bar.key}>
              <div
                data-cal-bar="true"
                className={`absolute h-[18px] rounded-sm px-1.5 flex items-center text-[10px] overflow-hidden z-[5] cursor-pointer transition-colors ${
                  isHovered ? style.hover : style.idle
                } ${isResizing ? 'opacity-90 ring-1 ring-cyan-400/60' : ''}`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%`, top: bar.resolvedTopPx }}
                onMouseEnter={() => setHoveredBarKey(bar.key)}
                onMouseLeave={() => setHoveredBarKey(null)}
                onClick={bar.onClick}
              >
                <span className="truncate font-medium">{bar.label}</span>
                {bar.meta && (
                  <span className="ml-auto shrink-0 opacity-70 tabular-nums pl-1">{bar.meta}</span>
                )}

                {/* Edge resize handles — only on resizable bars and only when
                    the parent supports onRangeResize. */}
                {bar.resizable && onResizeMouseDown && (
                  <>
                    <div
                      data-cal-bar="true"
                      className="absolute left-0 top-0 h-full cursor-col-resize"
                      style={{ width: RESIZE_HANDLE_WIDTH_PX }}
                      onMouseDown={(e) => onResizeMouseDown(bar, 'start', e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      data-cal-bar="true"
                      className="absolute right-0 top-0 h-full cursor-col-resize"
                      style={{ width: RESIZE_HANDLE_WIDTH_PX }}
                      onMouseDown={(e) => onResizeMouseDown(bar, 'end', e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </>
                )}
              </div>
              {isHovered && !isResizing && (
                <div
                  className="absolute z-20 px-2 py-1 rounded bg-black/90 text-white text-[10px] shadow-lg whitespace-nowrap pointer-events-none"
                  style={{ left: `${leftPct}%`, top: bar.resolvedTopPx - 24 }}
                >
                  {bar.label}
                  {bar.meta && ` — ${bar.meta}`}
                  {bar.tooltipSuffix && ` ${bar.tooltipSuffix}`}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Ghost bar for drag-to-create */}
        {ghost && (
          <div
            className="absolute h-[18px] rounded-sm bg-cyan-500/30 border border-dashed border-cyan-400 z-[6] pointer-events-none"
            style={{
              left: `${(ghost.startIdx / totalDays) * 100}%`,
              width: `${((ghost.endIdx - ghost.startIdx + 1) / totalDays) * 100}%`,
              top: ghost.topPx,
            }}
          />
        )}

        {/* Caller-supplied overlay (e.g. PlanningGrid's "show suggestions" buttons) */}
        {row.timelineOverlay?.({ periodBoundaries, totalDays, days })}
      </div>
    </div>
  );
}
